/**
 * MD-14 FE-LANDINGS - i18n registry + resolver
 *
 * Resolves landing copy per (slug, lang) with fallback chain:
 *   - exact match
 *   - lang fallback EN -> VN if EN entry is VN_ONLY sentinel (citizen)
 *   - VN if EN sibling missing entirely (defensive)
 */

import { citizenEn, citizenVi } from './landing-citizen';
import { expatEn, expatVi } from './landing-expat';
import {
  type Lang,
  type LandingCopy,
  type LandingSlug,
  VN_ONLY,
} from './landing-types';
import { smeEn, smeVi } from './landing-sme';
import { universalEn, universalVi } from './landing-universal';

export const VI_CATALOGS: Record<LandingSlug, LandingCopy> = {
  universal: universalVi,
  expat: expatVi,
  sme: smeVi,
  citizen: citizenVi,
};

export const EN_CATALOGS: Record<LandingSlug, ReturnType<typeof maskVnOnly>> = {
  universal: maskVnOnly(universalEn),
  expat: maskVnOnly(expatEn),
  sme: maskVnOnly(smeEn),
  citizen: maskVnOnly(citizenEn),
};

function maskVnOnly<T extends Record<string, string>>(catalog: T): T {
  return catalog;
}

/**
 * Resolve landing copy. If lang=en and a key is VN_ONLY, fall back to VN
 * for that key (per Sec.5 fallback chain VN -> EN -> key string).
 */
export function resolveLanding(slug: LandingSlug, lang: Lang): LandingCopy {
  const vi = VI_CATALOGS[slug];
  if (lang === 'vi') return vi;

  const en = EN_CATALOGS[slug];
  const out = {} as LandingCopy;
  for (const k of Object.keys(vi) as Array<keyof LandingCopy>) {
    const enValue = en[k];
    out[k] = !enValue || enValue === VN_ONLY ? vi[k] : (enValue as string);
  }
  return out;
}

/** AC-11 - verify every key present in EN catalog OR explicitly VN_ONLY. */
export function verifyKeyCoverage(slug: LandingSlug): { ok: boolean; missing: string[] } {
  const vi = VI_CATALOGS[slug];
  const en = EN_CATALOGS[slug];
  const missing: string[] = [];
  for (const k of Object.keys(vi)) {
    const enValue = (en as Record<string, string>)[k];
    if (enValue === undefined) missing.push(k);
  }
  return { ok: missing.length === 0, missing };
}
